import React from 'react';
import type { ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useSafeNavigation, useSafePathname, useSafeRouteParams } from '@sg/garnish';

import { useTrackEffect } from '@order/Telemetry';

import { LoadingAnimation, NotFoundView } from '../../components';
import { useMenuRedirect, usePermanentRedirects } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NotFoundScreen = () => {
  const navigation = useSafeNavigation<NativeStackNavigationProp<ParamListBase>>(); // prettier-ignore
  const routeParams = useSafeRouteParams<RouteParams>();
  const pathname = useSafePathname();

  // ─────────────────────────────────────────────────────────────────────

  const restaurantSlug = routeParams?.restaurantSlug ?? extractRestaurantSlug(pathname); // prettier-ignore

  // ─── Handlers ────────────────────────────────────────────────────────

  const hasPermanentRedirect = usePermanentRedirects({
    navigation: navigation ?? null,
    route: routeParams ?? null,
  });

  const isLoadingMenuRedirect = useMenuRedirect(hasPermanentRedirect, {
    navigation,
    restaurantSlug,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEffect('app.not_found', { path: routeParams?.path ?? pathname });

  // ─────────────────────────────────────────────────────────────────────

  if (isLoadingMenuRedirect || hasPermanentRedirect) {
    return <LoadingAnimation />;
  }

  return <NotFoundView />;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extracts a potential restaurant slug from a single-segment pathname.
 * e.g. "/west-3rd" → "west-3rd", "/foo/bar" → undefined
 */
function extractRestaurantSlug(pathname: string | null) {
  if (!pathname) return;

  const segments = pathname.replace(/^\//, '').split('/');

  if (segments.length !== 1 || segments[0] === '') return;

  return segments[0];
}

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  restaurantSlug?: string;
  path?: string;
};
